'use client';

import React from 'react';
import Image from 'next/image';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import 'swiper/css';
import { IHireAiPageType } from '@/components/type/hireUs/hireAiType';
import Container from '@/components/ui/Container';
import HeroSectionHeading from '@/components/ui/HeroSectionHeading';
import SectionHeader from '@/components/ui/SectionHeader';
import FaqsAccordion from '@/components/ui/FaqsAccordion';
import ClientTestimonial from '@/components/ui/ClientTestimonial';
import HireBenefits from '@/components/ui/HireBenefits';
import HireRoles from '@/components/ui/HireRoles';
import HireAiDevelopers from '@/components/ui/Hire_ai_developers';
import HireAiTable from '@/components/ui/HireAiTable';
import Button from '@/components/ui/Button';
import HireAiHeroForm from '@/components/ui/HireAiHeroForm';
import Marquee from 'react-fast-marquee';
import { logoIconsLight } from '@/components/utils/Constant';

type Props = {
  data: IHireAiPageType;
};

const PAGE_BG = '#0A0A0A';

const HireAi = ({ data }: Props) => {
  const fiveStats = data.stats.length === 5;

  return (
    // Gradient is on the whole page (same as AiSolution), so it blends smoothly
    <div style={{ background: PAGE_BG }} className="!font-opt">
      {/* 1. Hero */}

      <section
        className="relative overflow-hidden !font-opt"
        style={{
          background: data.hero.backgroundGradient,
        }}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-[180px] md:h-[260px]"
          style={{
            background: `linear-gradient(180deg, rgba(10,10,10,0) 0%, ${PAGE_BG} 100%)`,
          }}
        />

        <Container className="relative z-10 py-12 md:py-20 xl:py-[100px] min-[1600px]:pb-[110px] min-[1600px]:pt-[120px]">
          <div className="flex flex-col justify-between gap-10 lg:flex-row lg:items-center lg:gap-12">
            <div className="flex w-full max-w-[966px] flex-col gap-8 md:gap-12 lg:flex-1">
              <div className="flex flex-col gap-4">
                <h1
                  dangerouslySetInnerHTML={{ __html: data.hero.heading }}
                  className="max-w-[750px] font-opt text-3xl font-semibold uppercase leading-9 text-white md:text-4xl md:leading-[44px] xl:text-[42px] xl:leading-[1.2] [&_span]:!text-white"
                />
                <p className="font-opt text-base leading-6 text-white md:text-lg md:leading-7 xl:text-xl xl:leading-7 min-[1600px]:text-[22px] min-[1600px]:leading-[30px]">
                  {data.hero.description}
                </p>
              </div>

              <div className="flex flex-wrap gap-[15px]">
                <Button
                  btnName={data.hero.btnName}
                  redirectionLink={data.hero.btnLink}
                  isBackgroundLight
                />
                <Button
                  btnName={data.hero.secondaryBtnName}
                  redirectionLink={data.hero.secondaryBtnLink}
                  isWorkDetailSubPages
                />
              </div>
            </div>

            <HireAiHeroForm form={data.hero.form} />
          </div>
        </Container>
      </section>

      <Container className="mb-12 md:mb-20 lg:mb-[100px]">
        <div className="flex w-full min-w-0 flex-col items-start gap-6 md:gap-[25px] xl:flex-row 5xl:rounded-[24px]">
          <div className="w-full shrink-0 text-center xl:max-w-[350px] xl:text-start">
            <span className="block font-opt text-sm font-normal leading-5 text-white md:text-base">
              {data?.trustedClients}
            </span>
          </div>

          <div
            className="relative w-full min-w-0 flex-1 overflow-hidden"
            style={{
              maskImage:
                'linear-gradient(90deg, transparent 0%, black 15%, black 85%, transparent 100%)',
              WebkitMaskImage:
                'linear-gradient(90deg, transparent 0%, black 15%, black 85%, transparent 100%)',
            }}
          >
            {/* Left gradient overlay */}
            <div
              className="pointer-events-none absolute inset-y-0 -left-6 z-10 w-[60px] md:left-0"
              style={{
                background:
                  'linear-gradient(90deg, rgba(11,13,14,0.15) 0%, rgba(11,13,14,0) 100%)',
              }}
            />

            <Marquee
              speed={55}
              pauseOnHover={true}
              className="w-full overflow-hidden"
            >
              <div className="flex w-max shrink-0 items-center gap-6 px-4 md:gap-8 lg:gap-10">
                {logoIconsLight?.map((item, index) => (
                  <div
                    key={index}
                    className="flex shrink-0 items-center justify-center"
                  >
                    <Image
                      src={item}
                      alt={`Trusted client ${index + 1}`}
                      width={150}
                      height={50}
                      loading="lazy"
                      className="h-[35px] w-auto max-w-[70px] shrink-0 object-contain sm:h-[40px] sm:max-w-[80px] md:h-[50px] md:max-w-[100px]"
                    />
                  </div>
                ))}
              </div>
            </Marquee>

            {/* Right gradient overlay */}
            <div
              className="pointer-events-none absolute inset-y-0 -right-5 z-10 w-[60px] md:right-0"
              style={{
                background:
                  'linear-gradient(270deg, rgba(11,13,14,0.15) 0%, rgba(11,13,14,0) 100%)',
              }}
            />
          </div>
        </div>
      </Container>
      <div className="mb-12 px-[18px] md:mb-20 md:px-[40px] lg:mb-[100px] lg:px-0">
        <Swiper
          modules={[Autoplay]}
          slidesPerView={2}
          spaceBetween={13}
          loop={true}
          speed={600}
          autoplay={{
            delay: 2500,
            disableOnInteraction: false,
            pauseOnMouseEnter: false,
          }}
          breakpoints={{
            320: {
              slidesPerView: 1,
            },
            640: {
              slidesPerView: 2,
            },
            1024: {
              slidesPerView: 4,
            },
            1280: {
              slidesPerView: 5,
            },
          }}
          className="approach-swiper !h-full"
        >
          {data.approachCards.map((card, ind) => (
            <SwiperSlide key={ind} className="!h-auto">
              <article className="flex h-full flex-col gap-5 rounded-lg border border-white/15 bg-[#202426] p-4">
                <h3 className="border-b border-white/10 pb-3 text-lg font-medium text-white">
                  {card.title}
                </h3>

                <p className="text-base leading-relaxed text-white/75">
                  {card.description}
                </p>
              </article>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* 2. Stats */}
      <div className="px-[18px] md:px-[40px]">
        <Container className="mx-auto !max-w-[1200px] rounded-[20px] bg-gradient-to-r from-[#216fa7] via-[#078fca] to-[#00b4df] px-4 py-10 sm:!px-8 sm:py-12 lg:py-14">
          <div
            className={`grid grid-cols-2 gap-x-3 gap-y-9 lg:gap-4 ${
              fiveStats ? 'lg:grid-cols-5' : 'lg:grid-cols-4'
            }`}
          >
            {data.stats.map((stat) => (
              <div
                key={stat.label}
                className="flex flex-col items-center justify-center text-center text-white"
              >
                <p
                  className={`text-4xl font-normal leading-none tracking-tight sm:text-5xl ${
                    fiveStats ? 'lg:text-[52px]' : 'lg:text-[64px]'
                  }`}
                >
                  {stat.value}
                </p>
                <p className="mt-2 text-sm font-normal leading-5 sm:text-base lg:whitespace-nowrap">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </Container>
      </div>
      {/* 3. What You Get When You Hire AI Developers */}
      <HireBenefits data={data.hireBenefits} />

      {/* 4. AI Developers and Engineers You Can Hire */}
      <HireRoles data={data.hireRoles} />

      {/* 5. Comparison table */}
      <HireAiTable table={data.customModelComparison} />

      {/* 6. AI Engineer Skills We Vet Before You Interview */}
      <HireAiTable table={data.skillsTable} />

      {/* 7. Weighing One Dedicated Developer Against a Full AI Team? (CTA) */}
      <div className="pb-12 md:pb-20 lg:pb-[100px]">
        <div className="relative overflow-hidden bg-project-ai">
          {/* Image: sirf lg (1024px) aur upar, mobile/tablet pe hidden */}
          <div className="pointer-events-none absolute inset-0 mx-auto hidden max-w-[1920px] lg:block">
            <div className="absolute right-0 top-0 h-full w-[40vw] max-w-[892px]">
              <Image
                src={data.readyToTurnAi.imageUrl}
                alt={data.readyToTurnAi.imageAlt}
                fill
                sizes="(min-width: 1024px) 40vw, 0px"
                className="object-cover object-left"
              />
            </div>
          </div>

          <Container className="relative z-10 flex flex-col items-center gap-6 lg:flex-row lg:items-end lg:gap-6 xl:gap-10">
            <HeroSectionHeading
              heading={data.readyToTurnAi.heading}
              description={data.readyToTurnAi.description}
              btnName={data.readyToTurnAi.btnName}
              link={data.readyToTurnAi.btnLink}
              className="w-full !max-w-[979px] items-center py-12 text-center md:py-16 lg:min-w-0 lg:!max-w-[min(979px,58%)] lg:flex-none lg:items-start lg:py-[60px] lg:text-left xl:py-[80px] 3xl:py-[100px]"
              headingStyle="max-w-[853px] !text-[26px] !leading-[34px] sm:!text-3xl sm:!leading-10 md:!text-4xl lg:!text-[30px] lg:!leading-[38px] xl:!text-[36px] xl:!leading-[44px] 3xl:!text-[48px] 3xl:!leading-[56px]"
              descriptionStyle="!max-w-[979px] !text-base !leading-6 md:!text-lg md:!leading-7 xl:!text-[22px] xl:!leading-[32px]"
              isDark={true}
              btnStyle="bg-white hover:bg-white/30 hover:backdrop-blur-md"
            />
          </Container>
        </div>
      </div>
      {/* 8. Our Engagement Models to Hire AI Engineers */}
      <HireAiTable table={data.engagementTable} />

      {/* 9. How to Hire AI Developers in Five Steps */}
      <HireAiDevelopers data={data.hireProcess} />

      {/* 10. How We Secure and Govern AI Systems */}
      <div className="relative my-12 overflow-visible text-white md:my-20 lg:my-[100px]">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,110,135,0.28)_0%,rgba(0,0,0,0)_68%)]"
        />
        <Container className="mb-12 flex flex-col gap-10 md:mb-20 lg:mb-[100px] 4xl:gap-[60px]">
          <SectionHeader
            isSectionDark={true}
            headingText={data.secureAndGovern.headingText}
            className="items-center"
          />
          <div className="grid grid-cols-1 items-center gap-8 md:grid-cols-2 md:gap-x-10 md:gap-y-10 lg:grid-cols-[1fr_1.4fr_1fr] lg:gap-10">
            {/* Left features */}
            <div className="order-2 flex flex-col justify-center gap-8 md:gap-10 lg:order-1 lg:gap-12">
              {data.secureAndGovern.leftFeatures.map((item) => (
                <div key={item.title} className="flex flex-col gap-3 sm:gap-5">
                  <h3 className="text-xl font-semibold leading-6 text-white sm:text-2xl sm:leading-7">
                    {item.title}
                  </h3>
                  <p className="text-sm font-normal leading-5 text-white/90 sm:text-base sm:leading-6">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>

            {/* Center image */}
            <div className="relative order-1 flex items-center justify-center md:col-span-2 lg:order-2 lg:col-span-1">
              <div className="absolute left-1/2 top-1/2 h-48 w-48 -translate-x-1/2 -translate-y-1/2 rounded-full bg-cyan-400/15 blur-3xl sm:h-60 sm:w-60 lg:h-72 lg:w-72" />
              <div className="relative mx-auto w-full max-w-[340px] sm:max-w-[440px] md:max-w-[520px] lg:max-w-none">
                <Image
                  src={data.secureAndGovern.centerImage.url}
                  alt={data.secureAndGovern.centerImage.alt}
                  width={900}
                  height={700}
                  className="h-auto w-full object-contain drop-shadow-[0_0_28px_rgba(34,211,238,0.18)]"
                />
              </div>
            </div>

            {/* Right features */}
            <div className="order-3 flex flex-col justify-center gap-8 md:gap-10 lg:gap-12">
              {data.secureAndGovern.rightFeatures.map((item) => (
                <div
                  key={item.title}
                  className="group flex flex-col gap-3 sm:gap-5"
                >
                  <h3 className="text-xl font-semibold leading-6 text-white transition-colors duration-300 group-hover:text-cyan-300 sm:text-2xl sm:leading-7">
                    {item.title}
                  </h3>
                  <p className="text-sm font-normal leading-5 text-white/90 sm:text-base sm:leading-6">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </div>

      {/* 11. AI Technologies and Tools We Use */}
      <Container className="mb-12 flex flex-col gap-10 md:mb-20 lg:mb-[100px] 4xl:gap-[60px]">
        <SectionHeader
          isSectionDark={true}
          headingText={data.aiTechnologies.headingText}
          descriptionText={data.aiTechnologies.descriptionText}
          className="mx-auto max-w-[827px] items-center"
        />
        <div className="grid grid-cols-1 gap-px sm:grid-cols-2 lg:grid-cols-4">
          {data.aiTechnologies.categories.map((category, index) => {
            const rowSm = Math.floor(index / 2);
            const colSm = index % 2;
            const rowLg = Math.floor(index / 4);
            const colLg = index % 4;

            const darkSm = (rowSm + colSm) % 2 === 0;
            const darkLg = (rowLg + colLg) % 2 === 0;
            const darkMobile = index % 2 === 0;

            const bg = [
              darkMobile ? 'bg-[#242424]' : 'bg-black/10',
              darkSm ? 'sm:bg-[#242424]' : 'sm:bg-black/10',
              darkLg ? 'lg:bg-[#242424]' : 'lg:bg-black/10',
            ].join(' ');

            return (
              <div
                key={category.title}
                className={`flex min-w-0 flex-col gap-3 p-6 ${bg}`}
              >
                <h3 className="text-xl font-medium leading-7 text-white">
                  {category.title}
                </h3>
                <div className="flex flex-wrap gap-3">
                  {category.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center rounded-full border border-white/[0.06] bg-[#303030] px-2.5 py-1 text-sm font-normal leading-6 text-[#e5e5e5] transition-colors duration-200 hover:bg-[#444444]"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </Container>

      {/* 12. Industries cards swiper */}
      <div className="mb-12 flex flex-col gap-10 md:mb-20 lg:mb-[100px] 4xl:gap-[60px]">
        <Container>
          <SectionHeader
            headingText={data.aiIndustries.headingText}
            isSectionDark={true}
            descriptionText={data.aiIndustries.descriptionText}
            className="mx-auto max-w-[827px] items-center"
          />
        </Container>

        <Container>
          <Swiper
            modules={[Autoplay]}
            autoplay={{
              delay: 4000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            rewind={true}
            spaceBetween={16}
            slidesPerView={1}
            breakpoints={{
              768: { slidesPerView: 2, spaceBetween: 20 },
              1280: { slidesPerView: 3, spaceBetween: 20 },
            }}
            className="ai-project-swiper !h-full"
          >
            {data.aiIndustries.cards.map((card, index) => (
              <SwiperSlide
                key={`${card.title}-${index}`}
                className="!h-auto rounded-2xl border border-white/25"
              >
                <div className="flex h-full flex-col gap-2 sm:gap-3">
                  <Image
                    src={card.image}
                    alt={card.title}
                    width={500}
                    height={500}
                    className="w-full rounded-2xl object-contain"
                  />
                  <div className="flex h-full flex-col justify-between gap-4 p-4 sm:gap-5 sm:p-6">
                    <div className="flex flex-col gap-3 sm:gap-5">
                      <h3 className="text-xl font-semibold leading-6 text-white sm:text-2xl sm:leading-7">
                        {card.title}
                      </h3>
                      <p className="text-sm font-normal leading-5 text-white/80 sm:text-base">
                        {card.description}
                      </p>
                    </div>
                    <ul className="flex flex-col gap-3 sm:gap-4">
                      {card.services.map((service) => (
                        <li
                          key={service}
                          className="text-sm font-medium leading-5 text-white sm:text-base"
                        >
                          {service}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </Container>
      </div>

      {/* 13. Why Teams Choose Us for AI Builds */}
      <div className="flex w-full flex-col gap-10 4xl:gap-[60px]">
        <Container>
          <SectionHeader
            isSectionDark={true}
            headingText={data.whyChooseUs.headingText}
            descriptionText={data.whyChooseUs.descriptionText}
            className="items-center"
          />
        </Container>
        <Container>
          <Swiper
            modules={[Autoplay]}
            autoplay={{
              delay: 4000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            loop={data.whyChooseUs.items.length > 8}
            rewind={data.whyChooseUs.items.length <= 8}
            spaceBetween={20}
            slidesPerView={1}
            breakpoints={{
              640: { slidesPerView: 1.5, spaceBetween: 20 },
              768: { slidesPerView: 2, spaceBetween: 24 },
              1100: { slidesPerView: 3, spaceBetween: 28 },
              1280: { slidesPerView: 4, spaceBetween: 32 },
            }}
            className="whyChooseUs-swiper !h-full"
          >
            {data.whyChooseUs.items.map((item, index) => (
              <SwiperSlide
                key={index}
                className="!h-auto rounded-xl bg-whyChooseUs p-4 sm:p-6"
              >
                <div className="flex h-full flex-col gap-2 sm:gap-3">
                  <h3 className="text-lg font-semibold leading-6 text-white sm:text-xl sm:leading-7">
                    {item.title}
                  </h3>
                  <p className="text-sm font-normal leading-5 text-white/80 sm:text-base">
                    {item.description}
                  </p>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </Container>
      </div>

      {/* 14. What Clients Say */}
      <Container>
        <ClientTestimonial
          isCardBg={true}
          headingText={'WHAT CLIENTS SAY'}
          headingStyle={'!text-white'}
          className={'mx-auto max-w-[827px] items-center'}
          descriptionText={`Behind every testimonial is a business problem solved, a system improved, or a product successfully launched. Here is how our clients describe their experience working with Optimity Logics.`}
          descriptionStyle={'!text-white'}
        />
      </Container>
      {/* 15. FAQs */}
      <Container className="!max-w-[1200px] pb-12 md:pb-16 lg:pb-20">
        <FaqsAccordion data={data?.faqs} isDarkbg={true} />
      </Container>
    </div>
  );
};

export default HireAi;
